<template>
  <WorkspaceRoleDenied v-if="staffDenied" :area="t('usuarios.area')" />
  <div v-else class="workspace-usuarios-page">
    <!-- Header moderno -->
    <div class="modern-header">
      <div class="header-container">
        <!-- Breadcrumb e navegação -->
        <div class="breadcrumb-section">
          <nav class="breadcrumb">
            <button @click="$router.push('/dashboard')" class="breadcrumb-item">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
              </svg>
              {{ t('common.breadcrumb.dashboard') }}
            </button>
            <span class="breadcrumb-separator">/</span>
            <button @click="$router.push(`/workspace/${workspaceId}`)" class="breadcrumb-item">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
              </svg>
              {{ currentWorkspace?.nome || t('workspaceDashboard.defaultName') }}
            </button>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">{{ t('usuarios.breadcrumbCurrent') }}</span>
          </nav>
        </div>

        <!-- Título e informações principais -->
        <div class="title-section">
          <div class="title-content">
            <div class="title-icon">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
              </svg>
            </div>
            <div class="title-text">
              <h1 class="main-title">{{ t('usuarios.title') }}</h1>
              <p class="subtitle">{{ t('usuarios.subtitle') }}</p>
            </div>
          </div>
          
          <!-- Badge do tipo de workspace -->
          <div class="workspace-badge" :class="`type-${currentWorkspace?.tipo?.toLowerCase()}`">
            <div class="badge-icon">
              <svg v-if="currentWorkspace?.tipo === 'CRM'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
              <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
              </svg>
            </div>
            <span class="badge-text">{{ currentWorkspace?.tipo || 'TAREFAS' }}</span>
          </div>
        </div>

        <!-- Ações do header -->
        <div class="actions-section">
          <button
            v-if="podeGerenciarUsuarios"
            type="button"
            class="action-button secondary"
            :disabled="loading || membrosNoLimite"
            :title="membrosNoLimite ? t('usuarios.addMember.memberLimit') : ''"
            @click="openAddMemberModal"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            {{ t('usuarios.addMember.addUser') }}
          </button>
          <button 
            @click="gerarLinkConvite"
            class="action-button secondary"
            :disabled="loading"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-5 5v-5zM4 19h10v-2H4v2zM4 14h10v-2H4v2zM4 9h10V7H4v2zM4 4h10V2H4v2z"/>
            </svg>
            {{ t('usuarios.inviteLink') }}
          </button>
          <button 
            @click="$router.push(`/workspace/${workspaceId}/dashboard`)"
            class="action-button primary"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
            </svg>
            {{ t('usuarios.back') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Conteúdo principal -->
    <div class="page-content">
      <div class="tabs-header">
        <button
          class="tab-button"
          :class="{ active: activeTab === 'usuarios' }"
          @click="activeTab = 'usuarios'"
        >
          {{ t('usuarios.tabUsers') }}
        </button>
        <button
          v-if="showGrupoPermissaoTabNav"
          class="tab-button"
          :class="{ active: activeTab === 'grupos', 'tab-button--locked': gruposPlanBlocked }"
          @click="activeTab = 'grupos'"
        >
          <svg
            v-if="gruposPlanBlocked"
            class="tab-lock-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
          </svg>
          {{ t('usuarios.tabGroups') }}
        </button>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner"></div>
        <p class="loading-text">{{ t('usuarios.loading') }}</p>
      </div>

      <!-- Lista de usuários -->
      <div v-else-if="activeTab === 'usuarios'" class="usuarios-container">
        <!-- Header da lista -->
        <div class="usuarios-header">
          <div class="usuarios-stats">
            <div class="stat-item">
              <span class="stat-number">{{ usuarios.length }}</span>
              <span class="stat-label">{{ t('usuarios.totalMembers') }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-number">{{ convitesPendentes.length }}</span>
              <span class="stat-label">{{ t('usuarios.pendingInvites') }}</span>
            </div>
          </div>
        </div>

        <!-- Lista de usuários -->
        <div class="usuarios-list">
          <div 
            v-for="usuario in usuarios" 
            :key="usuario.id"
            :class="['usuario-card', `role-${(usuario.roleWorkspace || 'MEMBER').toLowerCase()}`]"
          >
            <div class="usuario-info">
              <div class="usuario-avatar">
                <img 
                  v-if="usuario.usuarioFotoPerfil" 
                  :src="usuario.usuarioFotoPerfil" 
                  :alt="usuario.usuarioNome"
                  class="avatar-image"
                  @error="handleImageError"
                />
                <div v-else class="avatar-placeholder">
                  {{ usuario.usuarioNome?.charAt(0)?.toUpperCase() }}
                </div>
              </div>
              <div class="usuario-details">
                <div class="usuario-name-row">
                  <h3 class="usuario-nome">{{ usuario.usuarioNome }}</h3>
                  <span class="status-badge" :class="getRoleClass(usuario.roleWorkspace)">
                    {{ getRoleLabel(usuario.roleWorkspace) }}
                  </span>
                </div>
                <p class="usuario-email">{{ usuario.usuarioEmail }}</p>
                <div class="usuario-status">
                  <span v-if="usuario.convitePendente" class="status-badge pending">
                    {{ t('usuarios.pendingInvite') }}
                  </span>
                </div>
              </div>
            </div>
            
            <div class="usuario-actions">
              <WorkspaceRoleSelect
                v-if="podeGerenciarUsuarios && usuario.roleWorkspace !== 'OWNER'"
                :model-value="usuario.roleWorkspace"
                :workspace-type="currentWorkspace?.tipo || 'TAREFAS'"
                :show-owner-option="podeTransferirPosse"
                :disabled="loading"
                :loading="loading"
                @change="(role) => atualizarPermissao(usuario.id, role)"
              />
              <span
                v-else-if="podeGerenciarUsuarios && usuario.roleWorkspace === 'OWNER'"
                class="owner-role-badge"
              >
                {{ t('usuarios.owner') }}
              </span>

              <select
                v-if="podeGerenciarUsuarios && usuario.roleWorkspace === 'MEMBER' && canPermissionGroups"
                :value="getSelectedGroupKey(usuario)"
                @change="atualizarGrupoMembro(usuario.id, $event.target.value)"
                class="permission-select group-select"
                :disabled="loading || permissionGroups.length === 0"
              >
                <option value="">{{ t('usuarios.noGroup') }}</option>
                <option
                  v-for="group in permissionGroups"
                  :key="group.key"
                  :value="group.key"
                >
                  {{ group.nome }}
                </option>
              </select>
              
              <!-- Botão remover -->
              <button 
                v-if="podeGerenciarUsuarios"
                @click="removerUsuario(usuario.id)"
                class="remove-button"
                :disabled="loading"
                :title="t('usuarios.removeUser')"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Estado vazio -->
          <div v-if="usuarios.length === 0" class="empty-state">
            <div class="empty-icon">
              <svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"/>
              </svg>
            </div>
            <h3 class="empty-title">{{ t('usuarios.emptyTitle') }}</h3>
            <p class="empty-description">{{ t('usuarios.emptyDescription') }}</p>
          </div>
        </div>
      </div>

      <div v-else-if="activeTab === 'grupos'" class="grupos-container">
        <PlanFeatureGate
          class="grupos-plan-gate"
          :blocked="gruposPlanBlocked"
          :title="t('usuarios.groupsPlanTitle')"
          :description="t('usuarios.groupsPlanDescription')"
        >
          <div class="usuarios-header">
            <div class="usuarios-stats">
              <div class="stat-item">
                <span class="stat-number">{{ permissionGroups.length }}</span>
                <span class="stat-label">{{ t('usuarios.groupsRegistered') }}</span>
              </div>
            </div>
            <button
              class="action-button primary"
              :disabled="!podeGerenciarUsuarios || gruposPlanBlocked"
              @click="openCreateGroup"
            >
              {{ t('usuarios.newGroup') }}
            </button>
          </div>

          <div class="usuarios-list">
            <div
            v-for="group in permissionGroups"
            :key="group.key"
            class="usuario-card group-card"
            @click="openEditGroup(group)"
          >
            <div class="usuario-info">
              <div class="usuario-avatar">
                <div class="avatar-placeholder">G</div>
              </div>
              <div class="usuario-details">
                <h3 class="usuario-nome">{{ group.nome }}</h3>
                <p class="usuario-email">{{ t('usuarios.clickToEdit') }}</p>
              </div>
            </div>
            <div class="usuario-actions">
              <button class="remove-group-btn" @click.stop="deleteGroup(group)" :disabled="!podeGerenciarUsuarios">{{ t('usuarios.delete') }}</button>
            </div>
          </div>

          <div v-if="permissionGroups.length === 0" class="empty-state">
            <h3 class="empty-title">{{ t('usuarios.noGroupsTitle') }}</h3>
            <p class="empty-description">{{ t('usuarios.noGroupsDescription') }}</p>
          </div>
          </div>
        </PlanFeatureGate>
      </div>
    </div>

    <AddWorkspaceMemberModal
      v-model="showAddMemberModal"
      :workspace-id="workspaceId"
      @added="carregarUsuarios"
    />

    <!-- Modal de Convite por Link -->
    <div v-if="showConviteModal" class="modal-overlay" @click="showConviteModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ t('usuarios.inviteModalTitle') }}</h3>
          <button @click="showConviteModal = false" class="modal-close">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="convite-info">
            <p class="convite-description">
              {{ t('usuarios.inviteDescription') }}
            </p>
            <div class="convite-details">
              <div class="detail-item">
                <span class="detail-label">{{ t('usuarios.workspaceLabel') }}</span>
                <span class="detail-value">{{ workspaceStore.currentWorkspace?.nome || t('common.loading') }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ t('usuarios.typeLabel') }}</span>
                <span class="detail-value">{{ workspaceStore.currentWorkspace?.tipo || 'Carregando...' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">{{ t('usuarios.expiresLabel') }}</span>
                <span class="detail-value">{{ t('usuarios.neverExpires') }}</span>
              </div>
            </div>
            <div class="link-container">
              <input 
                ref="linkInput"
                :value="linkConvite"
                readonly
                class="link-input"
              />
              <button @click="copiarLink" class="copy-button">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                </svg>
                {{ t('usuarios.copy') }}
              </button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="showConviteModal = false" class="btn-secondary">
            {{ t('common.close') }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showGroupModal" class="modal-overlay" @click="closeGroupModal">
      <div class="modal-content group-modal" @click.stop>
        <div class="modal-header">
          <h3>{{ editingGroupKey ? t('usuarios.editGroup') : t('usuarios.newGroup') }}</h3>
          <button @click="closeGroupModal" class="modal-close" :title="t('common.close')">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="group-form-card">
            <label class="field-label">{{ t('usuarios.groupName') }}</label>
            <input v-model="groupForm.nome" class="link-input" :placeholder="t('usuarios.groupNamePlaceholder')" />
          </div>

          <div class="section-title-row">
            <h4 class="section-title">{{ t('usuarios.kanbanPermissions') }}</h4>
            <span class="section-subtitle">{{ t('usuarios.toggleByGroup') }}</span>
          </div>
          <div class="permissions-grid">
            <div class="toggle-item" v-for="perm in permissionFields" :key="perm.key">
              <div class="toggle-item-info">
                <span class="toggle-item-label">{{ perm.label }}</span>
                <span class="toggle-item-desc">{{ perm.desc }}</span>
              </div>
              <button
                @click="toggleGroupPermission(perm.key)"
                class="toggle-switch"
                :class="{ active: !!groupForm.permissoes[perm.key] }"
              >
                <span class="toggle-switch-slider"></span>
              </button>
            </div>
          </div>

          <div class="columns-locks">
            <div class="columns-locks-header">
              <div>
                <h4>{{ t('usuarios.lockedColumnsTitle') }}</h4>
                <p class="columns-locks-hint">{{ t('usuarios.lockedColumnsHint') }}</p>
              </div>
              <span class="columns-locked-count">
                {{ t('usuarios.lockedCount', { count: groupForm.permissoes.lockedColumnIds.length }) }}
              </span>
            </div>

            <div class="columns-locks-list">
              <label v-for="column in kanbanColumns" :key="column.id" class="column-check-card">
                <div class="column-check-left">
                  <input
                    type="checkbox"
                    :checked="groupForm.permissoes.lockedColumnIds.includes(column.id)"
                    @change="toggleLockedColumn(column.id)"
                  />
                  <span class="column-dot" :style="{ backgroundColor: column.cor || '#6366f1' }"></span>
                  <span class="column-name">{{ column.nome }}</span>
                </div>
                <span v-if="groupForm.permissoes.lockedColumnIds.includes(column.id)" class="column-locked-tag">{{ t('usuarios.locked') }}</span>
              </label>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-secondary" @click="closeGroupModal">{{ t('common.cancel') }}</button>
          <button class="action-button primary" @click="saveGroup" :disabled="!podeGerenciarUsuarios">{{ t('common.save') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useWorkspaceStore } from '@/stores/workspace'
import api from '@/services/api'
import WorkspaceRoleDenied from '../components/workspace/WorkspaceRoleDenied.vue'
import WorkspaceRoleSelect from '../components/workspace/WorkspaceRoleSelect.vue'
import AddWorkspaceMemberModal from '../components/workspace/AddWorkspaceMemberModal.vue'
import PlanFeatureGate from '../components/plan/PlanFeatureGate.vue'
import { useWorkspaceStaffGuard } from '../composables/useWorkspaceStaffGuard'
import { usePlanoFeatures } from '../composables/usePlanoFeatures'

const route = useRoute()
const { t } = useI18n()
const { staffDenied, ensureAccess } = useWorkspaceStaffGuard(t('usuarios.area'))
const planoFeatures = usePlanoFeatures()
const workspaceStore = useWorkspaceStore()
const toast = useToast()

// Dados reativos
const loading = ref(false)
const usuarios = ref([])
const convitesPendentes = ref([])
const showConviteModal = ref(false)
const showAddMemberModal = ref(false)
const linkConvite = ref('')
const podeGerenciarUsuarios = ref(false)
const podeTransferirPosse = ref(false)
const activeTab = ref('usuarios')
const canPermissionGroups = computed(() => planoFeatures.can('permissionGroups'))
/** Aba visível em workspaces Task; conteúdo bloqueado fora de Pro/Corporativo */
const showGrupoPermissaoTabNav = computed(
  () => workspaceStore.currentWorkspace?.tipo !== 'CRM'
)
const gruposPlanBlocked = computed(() => !canPermissionGroups.value)
const permissionGroups = ref([])
const showGroupModal = ref(false)
const editingGroupKey = ref('')
const kanbanColumns = ref([])
const groupForm = ref({
  nome: '',
  permissoes: {
    canCreateColumns: false,
    canMoveColumns: false,
    canDeleteColumns: false,
    canMoveCards: true,
    canArchiveCards: false,
    showDescription: true,
    showTagsExpanded: true,
    showUsers: true,
    showActivityControls: true,
    showSchedule: true,
    showChecklist: true,
    showAttachments: true,
    showCreatedAt: true,
    showUpdatedAt: true,
    showFinanceiro: true,
    showTaskProgress: true,
    showCustomForms: true,
    lockedColumnIds: []
  }
})

const permissionFields = computed(() => [
  { key: 'canCreateColumns', label: t('usuarios.permCreateColumns'), desc: t('usuarios.permCreateColumnsDesc') },
  { key: 'canMoveColumns', label: t('usuarios.permMoveColumns'), desc: t('usuarios.permMoveColumnsDesc') },
  { key: 'canDeleteColumns', label: t('usuarios.permDeleteColumns'), desc: t('usuarios.permDeleteColumnsDesc') },
  { key: 'canMoveCards', label: t('usuarios.permMoveCards'), desc: t('usuarios.permMoveCardsDesc') },
  { key: 'canArchiveCards', label: t('usuarios.permArchiveCards'), desc: t('usuarios.permArchiveCardsDesc') },
  { key: 'showDescription', label: t('usuarios.permShowDescription'), desc: t('usuarios.permShowDescriptionDesc') },
  { key: 'showTagsExpanded', label: t('usuarios.permShowTags'), desc: t('usuarios.permShowTagsDesc') },
  { key: 'showUsers', label: t('usuarios.permShowUsers'), desc: t('usuarios.permShowUsersDesc') },
  { key: 'showActivityControls', label: t('usuarios.permShowActivity'), desc: t('usuarios.permShowActivityDesc') },
  { key: 'showSchedule', label: t('usuarios.permShowSchedule'), desc: t('usuarios.permShowScheduleDesc') },
  { key: 'showChecklist', label: t('usuarios.permShowChecklist'), desc: t('usuarios.permShowChecklistDesc') },
  { key: 'showAttachments', label: t('usuarios.permShowAttachments'), desc: t('usuarios.permShowAttachmentsDesc') },
  { key: 'showCreatedAt', label: t('usuarios.permShowCreatedAt'), desc: t('usuarios.permShowCreatedAtDesc') },
  { key: 'showUpdatedAt', label: t('usuarios.permShowUpdatedAt'), desc: t('usuarios.permShowUpdatedAtDesc') },
  { key: 'showFinanceiro', label: t('usuarios.permShowFinanceiro'), desc: t('usuarios.permShowFinanceiroDesc') },
  { key: 'showTaskProgress', label: t('usuarios.permShowTaskProgress'), desc: t('usuarios.permShowTaskProgressDesc') },
  { key: 'showCustomForms', label: t('usuarios.permShowCustomForms'), desc: t('usuarios.permShowCustomFormsDesc') }
])

// Computed
const workspaceId = computed(() => route.params.id)
const currentWorkspace = computed(() => workspaceStore.currentWorkspace)

const normalizePermissionGroupKey = (value) => {
  if (value === null || value === undefined) return ''
  const normalized = String(value).trim()
  if (!normalized || normalized === 'null' || normalized === 'undefined') {
    return ''
  }
  return normalized
}

const getSelectedGroupKey = (usuario) => {
  const key = normalizePermissionGroupKey(usuario?.permissionGroupKey)
  if (!key) return ''
  const exists = permissionGroups.value.some(group => String(group?.key) === key)
  return exists ? key : ''
}

// Métodos
const carregarUsuarios = async () => {
  try {
    loading.value = true
    const response = await api.get(`/workspace-acesso/workspace/${workspaceId.value}`)
    usuarios.value = (response.data || []).map((usuario) => ({
      ...usuario,
      permissionGroupKey: normalizePermissionGroupKey(usuario.permissionGroupKey)
    }))
    
    // Carregar convites pendentes
    await carregarConvitesPendentes()
    
    // Verificar permissões
    await verificarPermissoes()
    await loadPermissionGroups()
    await loadKanbanColumns()
    
  } catch (error) {
    console.error('Erro ao carregar usuários:', error)
    // TODO: Mostrar notificação de erro
  } finally {
    loading.value = false
  }
}

const loadKanbanColumns = async () => {
  try {
    const response = await api.get(`/colunas-kanban/workspace/${workspaceId.value}`)
    kanbanColumns.value = response.data || []
  } catch (error) {
    console.error('Erro ao carregar colunas do kanban:', error)
    kanbanColumns.value = []
  }
}

const loadPermissionGroups = async () => {
  try {
    if (!podeGerenciarUsuarios.value || !canPermissionGroups.value) {
      permissionGroups.value = []
      return
    }
    const response = await api.get(`/workspace-acesso/workspace/${workspaceId.value}/permission-groups`)
    permissionGroups.value = Array.isArray(response.data) ? response.data : []
  } catch (error) {
    console.error('Erro ao carregar grupos de permissão:', error)
    permissionGroups.value = []
  }
}

const carregarConvitesPendentes = async () => {
  try {
    const response = await api.get(`/workspace-acesso/workspace/${workspaceId.value}/convites-pendentes`)
    convitesPendentes.value = response.data || []
  } catch (error) {
    console.error('Erro ao carregar convites pendentes:', error)
  }
}

const verificarPermissoes = async () => {
  try {
    const response = await api.get(`/workspace-acesso/workspace/${workspaceId.value}/permissoes`)
    const data = response.data
    podeGerenciarUsuarios.value = data.podeGerenciarUsuarios
    podeTransferirPosse.value = data.podeTransferirPosse
  } catch (error) {
    console.error('Erro ao verificar permissões:', error)
  }
}

const membrosNoLimite = computed(() => {
  const max = planoFeatures.limits.value?.maxUsersPerWorkspace
  if (max == null || Number(max) < 0) return false
  const total = (usuarios.value?.length || 0) + (convitesPendentes.value?.length || 0)
  return total >= Number(max)
})

const openAddMemberModal = () => {
  if (membrosNoLimite.value) {
    const max = planoFeatures.limits.value?.maxUsersPerWorkspace
    toast.error(t('usuarios.toast.memberLimit', { max }))
    return
  }
  showAddMemberModal.value = true
}

const gerarLinkConvite = async () => {
  if (membrosNoLimite.value) {
    const max = planoFeatures.limits.value?.maxUsersPerWorkspace
    toast.error(t('usuarios.toast.memberLimit', { max }))
    return
  }
  try {
    console.log('Gerando link de convite para workspace:', workspaceId.value)
    console.log('Token atual:', localStorage.getItem('token'))
    
    // Usar o endpoint específico por workspace
    const requestData = {
      emailConvidado: "convite@workspace.com" // Email genérico para convites por link
    }
    
    console.log('Dados da requisição:', requestData)
    console.log('URL da requisição:', `/workspace-acesso/workspace/${workspaceId.value}/gerar-convite`)
    
    const response = await api.post(`/workspace-acesso/workspace/${workspaceId.value}/gerar-convite`, requestData)
    console.log('Resposta do servidor:', response.data)
    
    if (response.data && response.data.sucesso && response.data.linkConvite) {
      linkConvite.value = response.data.linkConvite
      showConviteModal.value = true
      console.log('✅ Link gerado com sucesso:', linkConvite.value)
      console.log('📋 Detalhes do convite:')
      console.log('   - Workspace:', response.data.workspaceNome, '(' + response.data.workspaceTipo + ')')
      console.log('   - Token:', response.data.token)
      console.log('   - Email:', response.data.emailConvidado)
    } else {
      console.error('❌ Resposta inválida do servidor:', response.data)
      toast.error(response.data?.mensagem || 'Resposta inválida do servidor')
    }
  } catch (error) {
    console.error('❌ Erro ao gerar link de convite:', error)
    console.error('Status:', error.response?.status)
    console.error('Data:', error.response?.data)
    
    if (error.response?.status === 401) {
      toast.error(t('usuarios.toast.unauthorizedInvite'))
    } else if (error.response?.status === 403) {
      toast.error(t('usuarios.toast.forbiddenInvite'))
    } else {
      toast.error(error.response?.data?.error || t('usuarios.toast.inviteError'))
    }
  }
}

const copiarLink = async () => {
  try {
    await navigator.clipboard.writeText(linkConvite.value)
    toast.success(t('usuarios.toast.linkCopied'))
  } catch (error) {
    console.error('Erro ao copiar link:', error)
    toast.error(t('usuarios.toast.copyError'))
  }
}

const atualizarPermissao = async (acessoId, novaPermissao) => {
  try {
    loading.value = true
    await api.put('/workspace-acesso/permissao', {
      acessoId: acessoId,
      roleWorkspace: novaPermissao
    })
    
    // Recarregar lista
    await carregarUsuarios()
    toast.success(t('usuarios.toast.roleUpdated'))
    
  } catch (error) {
    console.error('Erro ao atualizar permissão:', error)
    toast.error(error.response?.data?.error || t('usuarios.toast.roleUpdateError'))
  } finally {
    loading.value = false
  }
}

const atualizarGrupoMembro = async (acessoId, groupKey) => {
  try {
    loading.value = true
    await api.put(`/workspace-acesso/${acessoId}/permission-group`, { permissionGroupKey: groupKey || null })
    await carregarUsuarios()
    toast.success(t('usuarios.toast.groupUpdated'))
  } catch (error) {
    console.error('Erro ao atualizar grupo do membro:', error)
    toast.error(error.response?.data?.error || t('usuarios.toast.groupUpdateError'))
  } finally {
    loading.value = false
  }
}

const resetGroupForm = () => {
  groupForm.value = {
    nome: '',
    permissoes: {
      canCreateColumns: false,
      canMoveColumns: false,
      canDeleteColumns: false,
      canMoveCards: true,
      canArchiveCards: false,
      showDescription: true,
      showTagsExpanded: true,
      showUsers: true,
      showActivityControls: true,
      showSchedule: true,
      showChecklist: true,
      showAttachments: true,
      showCreatedAt: true,
      showUpdatedAt: true,
      showFinanceiro: true,
      showCustomForms: true,
      lockedColumnIds: []
    }
  }
}

const openCreateGroup = () => {
  editingGroupKey.value = ''
  resetGroupForm()
  showGroupModal.value = true
}

const openEditGroup = (group) => {
  editingGroupKey.value = group.key
  const defaultPerms = {
    canCreateColumns: false,
    canMoveColumns: false,
    canDeleteColumns: false,
    canMoveCards: true,
    canArchiveCards: false,
    showDescription: true,
    showTagsExpanded: true,
    showUsers: true,
    showActivityControls: true,
    showSchedule: true,
    showChecklist: true,
    showAttachments: true,
    showCreatedAt: true,
    showUpdatedAt: true,
    showFinanceiro: true,
    showCustomForms: true,
    lockedColumnIds: []
  }
  groupForm.value = {
    nome: group.nome || '',
    permissoes: {
      ...defaultPerms,
      ...(group.permissoes || {}),
      lockedColumnIds: Array.isArray(group?.permissoes?.lockedColumnIds) ? [...group.permissoes.lockedColumnIds] : []
    }
  }
  if (!groupForm.value.permissoes || !Array.isArray(groupForm.value.permissoes.lockedColumnIds)) {
    groupForm.value.permissoes = { ...(group.permissoes || {}), lockedColumnIds: [] }
  }
  showGroupModal.value = true
}

const closeGroupModal = () => {
  showGroupModal.value = false
  editingGroupKey.value = ''
}

const toggleGroupPermission = (key) => {
  groupForm.value.permissoes[key] = !groupForm.value.permissoes[key]
}

const toggleLockedColumn = (columnId) => {
  const current = groupForm.value.permissoes.lockedColumnIds || []
  if (current.includes(columnId)) {
    groupForm.value.permissoes.lockedColumnIds = current.filter(id => id !== columnId)
  } else {
    groupForm.value.permissoes.lockedColumnIds = [...current, columnId]
  }
}

const saveGroup = async () => {
  try {
    loading.value = true
    const payload = {
      nome: groupForm.value.nome,
      permissoes: groupForm.value.permissoes
    }
    if (!payload.nome?.trim()) {
      toast.warning(t('usuarios.toast.groupNameRequired'))
      return
    }
    if (editingGroupKey.value) {
      await api.put(`/workspace-acesso/workspace/${workspaceId.value}/permission-groups/${editingGroupKey.value}`, payload)
    } else {
      await api.post(`/workspace-acesso/workspace/${workspaceId.value}/permission-groups`, payload)
    }
    await loadPermissionGroups()
    closeGroupModal()
    toast.success(editingGroupKey.value ? t('usuarios.toast.groupUpdatedSuccess') : t('usuarios.toast.groupSaved'))
  } catch (error) {
    console.error('Erro ao salvar grupo:', error)
    toast.error(error.response?.data?.error || t('usuarios.toast.groupSaveError'))
  } finally {
    loading.value = false
  }
}

const deleteGroup = async (group) => {
  if (!confirm(t('usuarios.toast.confirmDeleteGroup', { name: group.nome }))) return
  try {
    loading.value = true
    await api.delete(`/workspace-acesso/workspace/${workspaceId.value}/permission-groups/${group.key}`)
    await loadPermissionGroups()
    await carregarUsuarios()
    toast.success(t('usuarios.toast.groupDeleted'))
  } catch (error) {
    console.error('Erro ao excluir grupo:', error)
    toast.error(error.response?.data?.error || t('usuarios.toast.groupDeleteError'))
  } finally {
    loading.value = false
  }
}

const removerUsuario = async (acessoId) => {
  if (!confirm(t('usuarios.toast.confirmRemoveUser'))) {
    return
  }
  
  try {
    loading.value = true
    await api.delete(`/workspace-acesso/${acessoId}`)
    
    // Recarregar lista
    await carregarUsuarios()
    toast.success(t('usuarios.toast.userRemoved'))
    
  } catch (error) {
    console.error('Erro ao remover usuário:', error)
    toast.error(error.response?.data?.error || t('usuarios.toast.userRemoveError'))
  } finally {
    loading.value = false
  }
}

const getRoleLabel = (role) => {
  const key = String(role || 'MEMBER').toUpperCase()
  const labels = {
    OWNER: t('usuarios.owner'),
    ADMIN: t('usuarios.staff'),
    MEMBER: t('usuarios.member')
  }
  return labels[key] || role
}

const getRoleClass = (role) => {
  const key = String(role || 'MEMBER').toUpperCase()
  const classes = {
    OWNER: 'owner',
    ADMIN: 'admin',
    MEMBER: 'member'
  }
  return classes[key] || 'member'
}

const handleImageError = (event) => {
  event.target.style.display = 'none'
  if (event.target.nextElementSibling) {
    event.target.nextElementSibling.style.display = 'flex'
  }
}

// Lifecycle (remonta via App.vue :key ao trocar workspace na mesma rota)
onMounted(async () => {
  await ensureAccess(workspaceId.value)
  if (staffDenied.value) return
  await workspaceStore.getWorkspaceById(workspaceId.value)
  await planoFeatures.loadForWorkspace(workspaceId.value)
  await carregarUsuarios()
})
</script>

<style scoped>
.workspace-usuarios-page {
  min-height: 100vh;
  background-color: var(--bg-primary);
  color: var(--text-primary);
}

/* Header moderno */
.modern-header {
  background: linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%);
  border-bottom: 1px solid var(--border-primary);
  padding: 24px 0;
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.breadcrumb-section {
  display: flex;
  align-items: center;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.breadcrumb-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
}

.breadcrumb-item:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.breadcrumb-separator {
  color: var(--text-muted);
  margin: 0 4px;
}

.breadcrumb-current {
  color: var(--text-primary);
  font-weight: 600;
}

.title-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.title-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--accent-primary) 0%, #7c3aed 100%);
  border-radius: 4px;
  color: white;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.title-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.main-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
}

.workspace-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
}

.workspace-badge.type-crm {
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
  color: white;
}

.workspace-badge.type-tarefas {
  background: linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%);
  color: white;
}

.badge-icon {
  display: flex;
  align-items: center;
}

.badge-text {
  font-weight: 600;
}

.actions-section {
  display: flex;
  gap: 12px;
  align-items: center;
}

.action-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.action-button.primary {
  background: linear-gradient(135deg, var(--accent-primary) 0%, #7c3aed 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.action-button.primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.action-button.secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
}

.action-button.secondary:hover {
  background: var(--bg-tertiary);
  transform: translateY(-1px);
}

.action-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Conteúdo da página */
.page-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 24px;
}

.tabs-header {
  display: flex;
  gap: 10px;
  margin-bottom: 18px;
  padding: 8px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(59, 130, 246, 0.03) 100%);
  border: 1px solid rgba(99, 102, 241, 0.18);
  border-radius: 4px;
  backdrop-filter: blur(6px);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}

.tab-button {
  padding: 10px 16px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--text-secondary);
  border-radius: 4px;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-button:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.tab-button.active {
  background: linear-gradient(135deg, var(--accent-primary) 0%, #2563eb 100%);
  color: #fff;
  border-color: rgba(37, 99, 235, 0.8);
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.28);
  transform: translateY(-1px);
}

.tab-button--locked {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.tab-button--locked:not(.active) {
  border-color: color-mix(in srgb, var(--accent-primary) 25%, var(--border-primary));
}

.tab-lock-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  opacity: 0.85;
}

.grupos-plan-gate {
  display: block;
}

.grupos-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.group-card {
  cursor: pointer;
  border-left: 3px solid var(--accent-primary);
}

.group-modal {
  max-width: 760px;
  max-height: 88vh;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  color: var(--text-secondary);
  font-size: 13px;
}

.permissions-grid {
  margin-top: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.toggle-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 12px;
  background: linear-gradient(180deg, var(--bg-secondary), var(--bg-primary));
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.toggle-item-info {
  display: flex;
  flex-direction: column;
}

.toggle-item-label {
  font-weight: 600;
  font-size: 13px;
}

.toggle-item-desc {
  font-size: 12px;
  color: var(--text-secondary);
}

.toggle-switch {
  width: 44px;
  height: 24px;
  border: none;
  border-radius: 999px;
  background: var(--bg-tertiary);
  position: relative;
  cursor: pointer;
}

.toggle-switch-slider {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  position: absolute;
  left: 3px;
  top: 3px;
  background: #fff;
  transition: all 0.2s ease;
}

.toggle-switch.active {
  background: var(--accent-primary);
}

.toggle-switch.active .toggle-switch-slider {
  left: 23px;
}

.columns-locks {
  margin-top: 18px;
  border-top: 1px solid var(--border-primary);
  padding-top: 14px;
  background: linear-gradient(180deg, rgba(99, 102, 241, 0.08), rgba(99, 102, 241, 0.02));
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 4px;
  padding: 14px;
}

.columns-locks-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 14px;
}

.columns-locks h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
}

.columns-locks-hint {
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.5;
}

.columns-locked-count {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.columns-locks-list {
  margin-top: 12px;
  max-height: 220px;
  overflow-y: auto;
  padding-right: 6px;
}

.column-check-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;
  padding: 10px 12px;
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  background: var(--bg-secondary);
  cursor: pointer;
}

.column-check-card:hover {
  border-color: var(--accent-primary);
  background: var(--bg-tertiary);
}

.column-check-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.column-name {
  font-size: 14px;
  font-weight: 600;
}

.column-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.6);
}

.column-locked-tag {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 999px;
  font-weight: 700;
  background: #fee2e2;
  color: #b91c1c;
}

.group-form-card {
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 12px;
  background: var(--bg-secondary);
}

.section-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 14px;
}

.section-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
}

.section-subtitle {
  font-size: 12px;
  color: var(--text-secondary);
}

.group-modal .modal-body {
  max-height: calc(88vh - 150px);
  overflow-y: auto;
}

.group-modal .modal-footer {
  position: sticky;
  bottom: 0;
  background: var(--bg-secondary);
}

.remove-group-btn {
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #fca5a5;
  background: #fee2e2;
  color: #b91c1c;
  font-weight: 600;
  cursor: pointer;
}

.remove-group-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 0;
  gap: 16px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--border-primary);
  border-top: 4px solid var(--accent-primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: var(--text-secondary);
  font-size: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.usuarios-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.usuarios-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  background: var(--bg-secondary);
  border-radius: 0 4px 4px 0;
  border: 1px solid var(--border-primary);
  box-shadow: 0 10px 26px rgba(15, 23, 42, 0.07);
  position: relative;
  overflow: hidden;
}

.usuarios-header::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #3b82f6 0%, #6366f1 100%);
}

.usuarios-stats {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}

.stat-item {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: 10px;
  row-gap: 2px;
  padding: 10px 12px;
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  background: linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-hover) 100%);
  min-width: 210px;
  box-shadow: 0 1px 0 color-mix(in srgb, var(--border-secondary) 65%, transparent);
}

.stat-number {
  grid-row: 1 / span 2;
  font-size: 24px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.02em;
  color: var(--accent-primary);
}

.stat-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.usuarios-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.usuario-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--bg-secondary);
  border-radius: 0 4px 4px 0;
  border: 1px solid var(--border-primary);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  position: relative;
  overflow: hidden;
}

.usuario-card:hover {
  background: var(--bg-secondary);
  border-color: var(--border-hover);
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.14);
  transform: translateY(-1px);
}

.usuario-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #94a3b8 0%, #64748b 100%);
}

.usuario-card.role-owner::before {
  background: linear-gradient(180deg, #f59e0b 0%, #d97706 100%);
}

.usuario-card.role-admin::before {
  background: linear-gradient(180deg, #3b82f6 0%, #2563eb 100%);
}

.usuario-card.role-member::before {
  background: linear-gradient(180deg, #10b981 0%, #059669 100%);
}

.usuario-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.usuario-avatar {
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(148, 163, 184, 0.25);
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.18);
  flex-shrink: 0;
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, var(--accent-primary) 0%, #7c3aed 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
}

.usuario-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.usuario-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.usuario-nome {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.usuario-email {
  font-size: 12px;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.3;
  word-break: break-word;
}

.usuario-status {
  display: flex;
  gap: 6px;
  margin-top: 1px;
  flex-wrap: wrap;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: 1px solid transparent;
}

.status-badge.owner {
  background: #fef3c7;
  color: #92400e;
  border-color: #fcd34d;
}

.status-badge.admin {
  background: #dbeafe;
  color: #1e40af;
  border-color: #93c5fd;
}

.status-badge.member {
  background: #ecfdf5;
  color: #065f46;
  border-color: #86efac;
}

.status-badge.member,
.status-badge.pending {
  backdrop-filter: blur(2px);
}

.status-badge.pending {
  background: #fee2e2;
  color: #dc2626;
  border-color: #fca5a5;
}

.usuario-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  position: relative;
  z-index: 1;
}

.owner-role-badge {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.15) 100%);
  border: 1px solid rgba(245, 158, 11, 0.45);
  color: #b45309;
}

.permission-select {
  padding: 7px 10px;
  min-height: 34px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.permission-select:hover {
  border-color: var(--border-hover);
  background: var(--bg-tertiary);
}

.permission-select:focus {
  outline: none;
  border-color: var(--accent-primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.16);
}

.group-select {
  min-width: 122px;
}

.remove-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  background: transparent;
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--text-secondary);
}

.remove-button:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.2);
}

.remove-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

@media (max-width: 920px) {
  .usuario-card {
    flex-direction: column;
    align-items: stretch;
    gap: 14px;
  }

  .usuario-actions {
    justify-content: flex-start;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 0;
  gap: 16px;
}

.empty-icon {
  color: var(--text-muted);
}

.empty-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.empty-description {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: var(--bg-secondary);
  border-radius: 4px;
  border: 1px solid var(--border-primary);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--border-primary);
}

.modal-header h3 {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.modal-body {
  padding: 20px;
}

.convite-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.convite-description {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 16px 0;
}

.convite-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  margin-bottom: 16px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
}

.detail-value {
  font-size: 13px;
  color: var(--text-primary);
  font-weight: 500;
}

.link-container {
  display: flex;
  gap: 8px;
}

.link-input {
  flex: 1;
  padding: 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  color: var(--text-primary);
  font-size: 14px;
  font-family: monospace;
}

.link-input:focus {
  outline: none;
  border-color: var(--accent-primary);
}

.copy-button {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  background: var(--accent-primary);
  border: none;
  border-radius: 4px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.copy-button:hover {
  background: #5B21B6;
  transform: translateY(-1px);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid var(--border-primary);
}

.btn-secondary {
  padding: 10px 20px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background: var(--bg-hover);
}

/* Responsividade */
@media (max-width: 768px) {
  .header-container {
    padding: 0 16px;
  }
  
  .title-section {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
  
  .actions-section {
    width: 100%;
    justify-content: space-between;
  }
  
  .page-content {
    padding: 24px 16px;
  }
  
  .tabs-header {
    padding: 6px;
    gap: 8px;
  }

  .tab-button {
    flex: 1;
    text-align: center;
    padding: 9px 10px;
    font-size: 13px;
  }

  .usuarios-header {
    padding: 14px;
  }

  .usuarios-stats {
    gap: 10px;
    width: 100%;
  }

  .stat-item {
    min-width: 100%;
  }
  
  .usuario-card {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
  
  .usuario-actions {
    width: 100%;
    justify-content: space-between;
  }

  .permissions-grid {
    grid-template-columns: 1fr;
  }

  .columns-locks-header {
    flex-direction: column;
  }
}
</style>